import {
  Button,
  Column,
  Columns,
  Notification,
} from '@codeparva/ui-components-library';
import { Form, Formik } from 'formik';
import { navigate } from 'gatsby';
import React, { useCallback, useEffect, useState } from 'react';
import { useWindowDimensions } from '../../../../../utilities/Dimensions';
import { mobilescreensize } from '../../../../../utilities/constants';
import { getBackButtonPathName } from '../../../helpers/paymentComponentHelper';
import { StepsComponent } from '../../Steps/Steps';
import { DynamicFormTypes } from '../DynamicFormTypes';
import { useSectionData } from '../hooks/dataContext';
import { getClassNameForButton } from './ClassNameSelector';
import { validateTenantInfo } from './ValidationSchema';
import { StepMaxCount } from './config';
import {
  checkMoveInReserveFailedCookie,
  checkMoveInReserveSuccessCookie,
  deleteTenantCookie,
  getCookies,
  removeMoveInReserveCookie,
} from './cookiesHelper';
import { frameSchema, handleFormValidations } from './formHelper';
import { getFormValidations } from './formValidations';
import { callDeactivateLedgerCall } from './apiHelpers';
import { fetchIsTenantDuplicate } from '@codeparva/v11-tfw-api-library';
import { getGlobalAndFacilityEncKeyFromContext } from '../../../../../utilities/encKeyHelpers';

export const FormikStepper = React.memo(
  React.forwardRef(
    ({ children, ...props }: DynamicFormTypes.TFormikStepper, ref: any) => {
      const context = useSectionData();
      const { MoveInPage } = StepMaxCount;
      let {
        gatsbyPathName,
        path,
        facilityID,
        location_state,
        location_city,
        facility_sms_id,
        requiredFields,
        backButtonRef,
        isAccountCreated,
        setIsAccountCreated,
        facilityContext,
      } = context;
      const { width } = useWindowDimensions();
      const [completed, setCompleted] = useState(false);
      const [step, setStep] = useState(0);
      const [showNotification, setShowNotification] = useState(false);
      const [tenantInfoValidations, setTenantInfoValidation] = useState<any[]>(
        []
      );

      const [eventState, setEventState] = useState<any>();
      const [screenHeight, setScreenHeight] = useState(500);
      const [alternateValidation, setAlternateValidation] = useState<any[]>([]);
      const [otherValidation, setOtherValidation] = useState<any[]>([]);
      const [res, setRes] = useState<any>({});
      const [clickedOnClear, setClickedOnClear] = useState(false);
      const [duplicate, setDuplicate] = useState(false);
      const [refState, setRefState] = useState<any>();

      const { facPublicKey } = getGlobalAndFacilityEncKeyFromContext(
        facilityContext,
        facilityID
      );

      const nonPaymentReservationSteps = !!getCookies('cToken') ? 2 : 3;
      const childrenArray = React.Children.toArray(
        children
      ) as React.ReactElement<DynamicFormTypes.FormikStepProps>[];
      const formDataValidations = getFormValidations(
        tenantInfoValidations,
        alternateValidation,
        otherValidation
      );
      const currentChild = childrenArray[step];

      useEffect(() => {
        return () => {
          const cTenantId = getCookies('cTenantId');
          const tenantId = cTenantId ? cTenantId.split('_')[1] : '';
          if (tenantId) {
            const isMoveinOrReserveFailed = checkMoveInReserveFailedCookie();
            const isMoveinOrReserveSuccess = checkMoveInReserveSuccessCookie();
            const ledgerId = getCookies('newLedgerId');
            if (
              (isMoveinOrReserveFailed || !isMoveinOrReserveSuccess) &&
              ledgerId
            ) {
              // deactivate the created ledger on change of tab without trying payment or with payment fail.
              const tenantToken = getCookies('cToken');
              (async () => {
                const [ledgerDeactivateErr] = await callDeactivateLedgerCall(
                  tenantId,
                  facilityID,
                  ledgerId,
                  tenantToken
                );
                if (ledgerDeactivateErr) {
                  console.error(
                    'Error in tenant deactivate call',
                    ledgerDeactivateErr
                  );
                }
              })();
            }
          }
          deleteTenantCookie('newLedgerId=');
          deleteTenantCookie('isNewTenant=');
          removeMoveInReserveCookie();
        };
      }, []);

      const flattenObject = (ob: any, index: number) => {
        if (ob.hasOwnProperty('label')) {
          if (index === 1) {
            setTenantInfoValidation(old => [...old, ob]);
          }
          if (index === 2) {
            setAlternateValidation(old => [...old, ob]);
          }
          if (index === 3) {
            setOtherValidation(old => [...old, ob]);
          }
        } else {
          for (const i in ob) {
            if (!ob.hasOwnProperty(i)) continue;
            if (typeof ob[i] === 'object' && ob[i] !== null) {
              flattenObject(ob[i], index);
            }
          }
        }
        return index === 1
          ? tenantInfoValidations
          : index === 2
          ? alternateValidation
          : otherValidation;
      };
      const scrollToView = () => {
        if (eventState && eventState.target) {
          typeof eventState.target.className === 'string' &&
            refState &&
            eventState.target.className.includes('submitButton') &&
            typeof window !== 'undefined' &&
            window.scrollTo({
              top: refState.offsetTop,
              behavior: 'smooth',
            });
        }
      };

      const renderError = useCallback(
        (step: number) => {
          switch (step) {
            case 0:
              let tenantInfoValidation = frameSchema(
                tenantInfoValidations,
                'TenantInfo'
              );
              let tenantInfoResponse = validateTenantInfo(tenantInfoValidation);
              setRes(tenantInfoResponse);
              scrollToView();

              break;
            case 1:
              let alternateInfoValidation = frameSchema(
                alternateValidation,
                'AlternateInfo'
              );
              let alternateInfoResponse = validateTenantInfo(
                alternateInfoValidation
              );
              let reservationOtherInfoValidation = frameSchema(
                otherValidation,
                'OtherInfo'
              );
              let reservationOtherInfoResponse = validateTenantInfo(
                reservationOtherInfoValidation
              );
              gatsbyPathName.includes('reserve')
                ? setRes(reservationOtherInfoResponse)
                : setRes(alternateInfoResponse);
              scrollToView();
              break;
            case 2:
              let otherInfoValidation = frameSchema(
                otherValidation,
                'OtherInfo'
              );
              let otherInfoResponse = validateTenantInfo(otherInfoValidation);
              setRes(otherInfoResponse);
              scrollToView();
              break;
            default:
              break;
          }
        },
        [tenantInfoValidations, step]
      );
      const formValues = handleFormValidations(formDataValidations, path);
      let initialValues = {};
      for (const [_, value] of Object.entries(formValues)) {
        initialValues = Object.assign(initialValues, { ...value });
      }
      const count = Object.keys(formValues[0]);

      useEffect(() => {
        setRefState(ref.current);
        document.addEventListener('click', e => {
          setEventState(e);
        });

        scrollToView();

        async function fetchData() {
          context.sectionData.map((item: { data: any; id: number }) => {
            flattenObject(item.data, item.id);
          });
          renderError(step);
        }

        try {
          fetchData();
        } catch (error) {
          console.error(error);
        }
        setScreenHeight(screen.height);
        return () => {
          setEventState(undefined);
          window.removeEventListener('click', e => {
            setEventState(e);
          });
          window.removeEventListener('scroll', () => {});
        };
      }, [children, step, count.length, ref]);

      function isLastStep() {
        if (
          gatsbyPathName.includes('reserve') &&
          childrenArray.length === nonPaymentReservationSteps
        ) {
          return step === childrenArray.length - 1;
        } else {
          return step === childrenArray.length - 2;
        }
      }

      const clearForm = (formValues: any) => {
        setClickedOnClear(true);
        for (let [key] of Object.entries(formValues)) {
          formValues[key] = '';
        }
        return formValues;
      };

      const onKeyDown = (keyEvent: any) => {
        if ((keyEvent.charCode || keyEvent.keyCode) === 13) {
          keyEvent.preventDefault();
        }
      };

      const handleCloseButton = () => setDuplicate(false);

      const handleRedirectToLogin = () => {
        navigate(
          `/${location_state.toLowerCase()}/${location_city.toLowerCase()}/account/login`,
          {
            state: { facility_sms_id: facility_sms_id },
          }
        );
      };

      const handleOkay = () => {
        setIsAccountCreated(false);
        const lastStep =
          childrenArray && childrenArray[childrenArray.length - 1];
        if (lastStep?.props?.label.toLowerCase().includes('payment')) {
          setStep(s => s + 1);
        }
      };

      const goToNextPage = () => {
        setShowNotification(false);
        setStep(step => step + 1);
      };
      const filterRequiredFields = (fieldName: string) => {
        const res = requiredFields.filter((el: any) => {
          return el.name.includes(fieldName);
        });
        return res;
      };

      const getDuplicateStatus = async (values: any) => {
        if (
          Object.keys(values).includes('TenantInfo_EmailAddress') ||
          Object.keys(values).includes('TenantInfo_CellPhone') ||
          (Object.keys(values).includes('TenantInfo_AddressLine1') &&
            Object.keys(values).includes('TenantInfo_LastName'))
        ) {
          let entries = Object.entries(values);
          let termsFilter: any = {};

          entries.map(([key, val]) => {
            if (
              key.includes('TenantInfo_EmailAddress') ||
              key.includes('TenantInfo_CellPhone') ||
              key.includes('TenantInfo_AddressLine1') ||
              Object.keys(values).includes('TenantInfo_LastName')
            ) {
              switch (true) {
                case key.includes('TenantInfo_EmailAddress'):
                  termsFilter.email = key.includes('TenantInfo_EmailAddress')
                    ? {
                        email_address: [val],
                      }
                    : null;
                  break;
                case key.includes('TenantInfo_CellPhone'):
                  termsFilter.phone = key.includes('TenantInfo_CellPhone')
                    ? {
                        primary_number: [val],
                      }
                    : null;
                  break;
                case key.includes('TenantInfo_LastName'):
                  termsFilter.name = key.includes('TenantInfo_LastName')
                    ? { last_name: [val] }
                    : null;
                  break;
                case key.includes('TenantInfo_AddressLine1'):
                  termsFilter.address = key.includes('TenantInfo_AddressLine1')
                    ? { line1: [val] }
                    : null;
                  break;
              }
            }
          });

          const [
            ,
            {
              data: { data: { is_duplicate: isDuplicate = false } = {} } = {},
            } = {},
          ] = await fetchIsTenantDuplicate({
            termsFilter: termsFilter,
            facilityId: facilityID,
            facPublicKey,
          });

          if (
            (getCookies('cToken') == '' || getCookies('cToken') == undefined) &&
            isDuplicate
          ) {
            setDuplicate(true);
          }else {
            goToNextPage();
          }
        } 
      };

      const handleEmailId = () => {
        const inputElement = document.getElementById(
          'TenantInfo_EmailAddress'
        ) as HTMLInputElement;
        if (inputElement) {
          inputElement.disabled = true;
        }
      };

      const handleUserName = (values: any) => {
        for (const [key, value] of Object.entries(values)) {
          if (key === 'TenantInfo_EmailAddress') {
            if (value) {
              values['OtherInfo_EmailAddress'] = value;
              const inputElement = document.getElementById(
                'OtherInfo_EmailAddress'
              ) as HTMLInputElement;
              if (inputElement) {
                inputElement.disabled = true;
              }
              return;
            }
          }
        }
      };

      return (
        <Formik
          {...props}
          validateOnChange={false}
          enableReinitialize={!clickedOnClear}
          validateOnBlur={false}
          validationSchema={res[0]}
          initialValues={initialValues}
          onSubmit={async (values, helpers) => {
            const isMoveIn = gatsbyPathName.includes('move-in');
            const isReserve = gatsbyPathName.includes('reserve');
            if (isLastStep()) {
              await props.onSubmit(values, helpers);
              if (
                getCookies('isNewTenant') == '' ||
                getCookies('isNewTenant') == undefined
              )
                setStep(s => s + 1);
            } else if (step === childrenArray.length - 1) {
              setCompleted(true);
            } else {
              renderError(step);
              if (step === 0 && (isMoveIn || isReserve)) {
                setShowNotification(true);
                getDuplicateStatus(values);
              } else {
                setStep(s => s + 1);
              }
              helpers.setTouched({});
            }
          }}
        >
          {({ isSubmitting, values, resetForm }) => {
            switch (step) {
              case 0:
                scrollToView();
                !!getCookies('cToken') && handleEmailId();
                const tenantInfoRes = filterRequiredFields('TenantInfo');
                sessionStorage.setItem(
                  'requiredFields',
                  JSON.stringify(tenantInfoRes)
                );
                requiredFields = tenantInfoRes;
                break;
              case 1:
                gatsbyPathName.includes('move-in') && scrollToView();
                gatsbyPathName.includes('reserve') &&
                  (getCookies('cToken') == '' ||
                    getCookies('cToken') == undefined) &&
                  handleUserName(values);
                const res = gatsbyPathName.includes('reserve')
                  ? filterRequiredFields('OtherInfo')
                  : filterRequiredFields('AlternateInfo');
                sessionStorage.setItem('requiredFields', JSON.stringify(res));
                requiredFields = res;
                break;
              case 2:
                gatsbyPathName.includes('move-in') && scrollToView();
                gatsbyPathName.includes('move-in') &&
                  (getCookies('cToken') == '' ||
                    getCookies('cToken') == undefined) &&
                  handleUserName(values);
                let response = filterRequiredFields('OtherInfo');
                sessionStorage.setItem(
                  'requiredFields',
                  JSON.stringify(response)
                );
                break;
              default:
                scrollToView();
                sessionStorage.setItem(
                  'requiredFields',
                  JSON.stringify(requiredFields)
                );
            }

            return (
              <Form
                autoComplete="off"
                className="pb-5 FormikStepper"
                onKeyDown={onKeyDown}
              >
                {duplicate && (
                  <Notification
                    style={{ top: `calc(${screenHeight / 2} - 160px )` }}
                  >
                    <Button
                      className="delete"
                      backgroundColor="grey"
                      onClick={handleCloseButton}
                    ></Button>
                    <p className="pb-2">
                      There is already an existing account with this
                      information. Please contact the facility if you have
                      forgotten your password.
                    </p>
                    <div className="is-flex is-justify-content-center ">
                      <Button
                        className="ml-2 cp-primary-button-highlighted buttonColor"
                        onClick={handleRedirectToLogin}
                      >
                        Login
                      </Button>
                    </div>
                  </Notification>
                )}
                {isAccountCreated && (
                  <Notification
                    className="is-flex is-flex-direction-column is-align-items-center"
                    style={{ top: `calc(${screenHeight / 2} - 160px )` }}
                  >
                    <div className="tickMarkContainer is-flex is-align-items-center is-justify-content-center mb-2">
                      <div className="tickSymbol"></div>
                    </div>
                    <h3 className="py-2 accountCreatedHeader">
                      Account created!
                    </h3>
                    <p className="pb-6 pt-2">
                      An account has been successfully created. You can login
                      using the same credentials on the website.
                    </p>
                    <div className="is-flex is-justify-content-center ">
                      <Button
                        className="ml-2 cp-primary-button-highlighted buttonColor"
                        onClick={handleOkay}
                      >
                        Okay
                      </Button>
                    </div>
                  </Notification>
                )}
                <StepsComponent
                  activeStep={step}
                  childrenArray={childrenArray}
                  completed={completed}
                  formType={
                    childrenArray.length > MoveInPage ? 'Rental' : 'Reservation'
                  }
                />
                <div
                  className={`${step === childrenArray.length - 1 &&
                    'DynamicSectionContainerDivTabletLastStep'} DynamicSectionContainerDiv`}
                >
                  {currentChild}
                </div>
                <Columns
                  className="DynamicSectionContainer is-mobile"
                  marginless
                  multiline={false}
                >
                  <Column className="is-5-tablet is-7-desktop columnOne">
                    <>
                      {step > 0 && step !== childrenArray.length - 1 && (
                        <Button
                          type="button"
                          className="backButton cp-primary-button-highlighted"
                          disabled={isSubmitting || isAccountCreated}
                          domRef={backButtonRef}
                          onClick={() => {
                            ref.current &&
                              ref.current.scrollIntoView({
                                block: 'start',
                                inline: 'start',
                                behavior: 'smooth',
                              });
                            if (step === childrenArray.length - 1) {
                              {
                                window.history.pushState(
                                  {},
                                  '',
                                  getBackButtonPathName(
                                    location.pathname,
                                    gatsbyPathName
                                  )
                                );
                              }
                              setStep(s => s - 1);
                            } else {
                              setStep(s => s - 1);
                            }
                          }}
                        >
                          Back
                        </Button>
                      )}
                    </>
                  </Column>
                  <Column
                    className={`is-flex columnTwo is-7-tablet is-5-desktop ${getClassNameForButton(
                      step,
                      childrenArray,
                      gatsbyPathName
                    )}`}
                  >
                    {width > mobilescreensize &&
                      step !== childrenArray.length - 2 &&
                      step !== childrenArray.length - 1 && (
                        <Button
                          outlined={true}
                          disabled={isSubmitting || duplicate}
                          className="ClearButton cp-outline-button-highlighted"
                          type="button"
                          onClick={() => {
                            ref.current &&
                              ref.current.scrollIntoView({
                                block: 'start',
                                inline: 'start',
                                behavior: 'smooth',
                              });
                            resetForm({
                              values: {
                                ...values,
                                ...clearForm(formValues[step]),
                              },
                            });
                            setShowNotification(false);
                          }}
                        >
                          Clear Form
                        </Button>
                      )}
                    {((gatsbyPathName.includes('reserve') &&
                      childrenArray.length === nonPaymentReservationSteps) ||
                      step !== childrenArray.length - 1) && (
                      <Button
                        disabled={
                          isSubmitting ||
                          duplicate ||
                          showNotification ||
                          isAccountCreated
                        }
                        type="submit button"
                        className="submitButton cp-primary-button-highlighted"
                        data-cy="submitButtonDynamicForm"
                        onClick={() => {
                          document.addEventListener('click', scrollToView);
                        }}
                      >
                        {isSubmitting
                          ? 'Submitting'
                          : isLastStep()
                          ? 'Confirm'
                          : 'Next'}
                      </Button>
                    )}
                  </Column>
                </Columns>
              </Form>
            );
          }}
        </Formik>
      );
    }
  )
);
